import { FC, ReactNode, useState } from 'react';
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
} from '@carbon/react';
import type { DocumentResponse } from 'types/api-responses';
import shell from 'pages/afe-catalog/structuring/structuring-module.module.scss';
import styles from 'pages/afe-catalog/structuring/identification.module.scss';

// Read-only county-index vendor record for a document. All values come off the
// already-fetched document payload (no resource of its own), so there is no
// loading state — the section only renders once the document has loaded.

// Human labels for the vendor SourceDocument#source keys. Document#vendor_source
// never returns the internal 'autoprovisioner' artifact; a nil source is an
// interactive upload, shown as "Uploaded manually". Unmapped sources
// (e.g. halfile_go_forward) fall back to their raw key.
const VENDOR_SOURCE_LABELS: Record<string, string> = {
  'halfile': 'HalFile',
  'di_courthouse': 'DI Courthouse',
  'county-clerk': 'County Clerk',
  'airtable': 'Airtable',
  'tracts': 'Tracts',
  'govos': 'GovOS',
};

const MANUAL_UPLOAD_LABEL = 'Uploaded manually';

const LEGAL_DESCRIPTION_PREVIEW_CHARS = 200;
const EMPTY = '—';

const HEADERS = [
  { key: 'field', header: 'Field' },
  { key: 'value', header: 'Value' },
];

export const VendorInformationSection: FC<{ document: DocumentResponse }> = ({
  document,
}) => {
  const [legalExpanded, setLegalExpanded] = useState(false);

  // A nil source means an interactive upload with no county-index vendor record.
  const vendorLabel = document.vendorSource
    ? (VENDOR_SOURCE_LABELS[document.vendorSource] ?? document.vendorSource)
    : MANUAL_UPLOAD_LABEL;

  const title = (
    <div className={shell.overviewSectionTitleRow}>
      <h4 className={shell.overviewSectionTitle}>Vendor Information</h4>
    </div>
  );

  const grantors = document.grantors
    .map((grantor) => grantor.name)
    .filter(Boolean)
    .join(', ');
  const grantees = document.grantees
    .map((grantee) => grantee.name)
    .filter(Boolean)
    .join(', ');

  const legal = document.legalDescription ?? '';
  const legalIsLong = legal.length > LEGAL_DESCRIPTION_PREVIEW_CHARS;
  const legalText =
    legalExpanded || !legalIsLong
      ? legal
      : `${legal.slice(0, LEGAL_DESCRIPTION_PREVIEW_CHARS)}…`;

  const rows: { field: string; value: ReactNode }[] = [
    { field: 'Book Type', value: document.bookType || EMPTY },
    { field: 'Volume', value: document.volume || EMPTY },
    { field: 'Page', value: document.page || EMPTY },
    { field: 'Instrument Number', value: document.instrumentNumber || EMPTY },
    { field: 'File Date', value: document.fileDate || EMPTY },
    { field: 'Grantor(s)', value: grantors || EMPTY },
    { field: 'Grantee(s)', value: grantees || EMPTY },
    {
      field: 'Legal Description',
      value: legal ? (
        <>
          {legalText}
          {legalIsLong && (
            <Button
              kind="ghost"
              size="sm"
              onClick={() => setLegalExpanded((expanded) => !expanded)}
            >
              {legalExpanded ? 'Show less' : 'Show more'}
            </Button>
          )}
        </>
      ) : (
        EMPTY
      ),
    },
    { field: 'Source', value: vendorLabel },
  ];

  return (
    <div className={shell.overviewBody}>
      {title}
      <TableContainer>
        <Table size="sm" className={styles.identTable}>
          <TableHead>
            <TableRow>
              {HEADERS.map((header) => (
                <TableHeader key={header.key}>{header.header}</TableHeader>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.field}>
                <TableCell>{row.field}</TableCell>
                <TableCell>{row.value}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};
